'use client';

import Image from 'next/image';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { IconChevronRight } from '@repo/svgs';
import type { Chat } from '@repo/api';
import { cn } from '@/lib/cn';
import { bathroomLabel, formatCurrency } from '@repo/utils';

interface ChatListItemProps {
  chat: Chat;
  isSelected?: boolean;
  onClick: () => void;
}

// Format date to match Figma design: "Today", "Yesterday", or "M/D/YY"
function formatMessageDate(dateString: string): string {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const messageDate = new Date(date);
  messageDate.setHours(0, 0, 0, 0);

  if (messageDate.getTime() === today.getTime()) {
    return 'Today';
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    const month = messageDate.getMonth() + 1;
    const day = messageDate.getDate();
    const year = messageDate.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  }
}

export function ChatListItem({ chat, isSelected, onClick }: ChatListItemProps) {
  const { unit, latestMessage, hasUnreadMessages } = chat;
  // Extract unit data - handle both full unit object and minimal unit object
  const unitData = unit as any;
  const fullAddress = unitData?.fullAddress || unitData?.address || '';
  const numBedrooms = unitData?.numBedrooms ?? 0;
  const images = unitData?.images || [];
  const activeListing = unitData?.activeListing;
  const pastListings = unitData?.pastListings || [];
  const lastActiveListing = unitData?.lastActiveListing;

  const lastMessageTime = latestMessage?.atCreated || new Date().toISOString();
  const formattedDate = formatMessageDate(lastMessageTime);
  const isToday = formattedDate === 'Today';

  // ListingCard-style price display:
  // If grossRent & netRent exist and netRent < grossRent -> show both (gross struck through).
  // Otherwise show netRent (or best available fallback).
  const grossRentValue = activeListing?.grossRent ?? null;
  const netRentValue =
    activeListing?.netRent ?? activeListing?.rent ?? unitData?.previousListingRent ?? null;

  const grossRent =
    typeof grossRentValue === 'number'
      ? grossRentValue
      : typeof grossRentValue === 'string'
        ? Number(grossRentValue)
        : null;
  const netRent =
    typeof netRentValue === 'number'
      ? netRentValue
      : typeof netRentValue === 'string'
        ? Number(netRentValue)
        : null;

  const showTwoPrices =
    Number.isFinite(grossRent) &&
    Number.isFinite(netRent) &&
    (netRent as number) < (grossRent as number);

  const displayNet = Number.isFinite(netRent) ? (netRent as number) : '';
  const displayGross = Number.isFinite(grossRent) ? (grossRent as number) : '';

  // Format beds/baths
  const displayBedrooms =
    numBedrooms === 0 ? 'Studio' : numBedrooms === 1 ? '1 bed' : `${numBedrooms} beds`;
  const displayBathrooms = bathroomLabel(
    unitData?.numBathrooms ?? 0,
    unitData?.numHalfBathrooms ?? 0,
  );
  const bedsBaths = `${displayBedrooms}, ${displayBathrooms}`;

  const addressParts = fullAddress?.split(',').map((part: string) => part.trim()) || [];
  const streetAddress = addressParts[0] || '';

  const [randomNumber, setRandomNumber] = useState<number | null>(null);

  useEffect(() => {
    if (!randomNumber) {
      const randomNum = Math.floor(Math.random() * 5) + 1;
      setRandomNumber(randomNum);
    }
  }, [randomNumber]);

  const memoizedRandomImage = useCallback(() => randomNumber, [randomNumber]);

  // Get the first image URL or use a placeholder
  const imageUrl =
    images && images.length > 0
      ? images[0]?.url || images[0]?.imageM || images[0]?.imageS || `/listing-card-placeholder.png`
      : `/listing-card-placeholder.png`;

  return (
    <div
      className={cn(
        'flex w-full cursor-pointer rounded-xl gap-3  px-3 py-4 transition-colors',
        isSelected ? 'bg-active border-transparent' : 'bg-primary hover:bg-secondary',
      )}
      onClick={onClick}
    >
      {/* Property Image */}
      <div className="relative h-16 w-16 flex-shrink-0">
        <Image
          src={imageUrl}
          alt={streetAddress}
          fill
          className="rounded-lg object-cover"
          sizes="64px"
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex min-w-0 flex-col gap-1">
        {/* Address and Timestamp */}
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              'truncate text-sm',
              hasUnreadMessages ? 'text-primary text-body-2-high' : 'text-secondary text-body-2',
            )}
          >
            {streetAddress}
          </span>
          {/* Timestamp with blue dot for Today when selected */}
          <span
            className={cn(
              'inline-flex items-center gap-1 text-xs flex-shrink-0 text-secondary md:hidden',
              hasUnreadMessages ? 'text-primary text-body-2-high' : 'text-secondary text-body-2',
            )}
          >
            {hasUnreadMessages && <span className="h-2 w-2 rounded-full bg-accent"></span>}
            {formattedDate}
            <IconChevronRight className="w-3 h-3 text-secondary" />
          </span>
          <span
            className={cn(
              'hidden md:inline-flex items-center gap-1 text-xs flex-shrink-0',
              hasUnreadMessages ? 'text-primary text-body-2-high' : 'text-secondary text-body-2',
            )}
          >
            {hasUnreadMessages && <span className="h-2 w-2 rounded-full bg-accent"></span>}
            {formattedDate}
            <IconChevronRight className="w-3 h-3 text-secondary" />
          </span>
        </div>

        {/* Beds/Baths */}
        <span
          className={cn(
            'text-xs',
            hasUnreadMessages ? 'text-primary text-body-2-high' : 'text-secondary text-body-2',
          )}
        >
          {bedsBaths}
        </span>

        {!!activeListing ? (
          <div className="flex items-center gap-1.5">
            {showTwoPrices && (
              <span
                className={cn(
                  'text-caption line-through',
                  hasUnreadMessages ? 'text-primary' : 'text-secondary',
                )}
              >
                {formatCurrency(displayGross)}
              </span>
            )}
            <span
              className={cn(
                'text-body-1-high font-semibold',
                hasUnreadMessages ? 'text-primary' : 'text-secondary',
              )}
            >
              {formatCurrency(displayNet || displayGross)}
            </span>
          </div>
        ) : (
          <span className="text-secondary text-caption">Closed</span>
        )}

        {/* Price */}
      </div>
    </div>
  );
}
